import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  useParams,
  useNavigate,
  Outlet,
  useOutletContext,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import BetaNavbar from "./components/BetaNavbar";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";

const V2Layout = () => {
  const { t } = useTranslation();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const parentContext = useOutletContext() || {};

  const [workspaces, setWorkspaces] = useState(parentContext.workspaces || []);
  const [refreshKey, setRefreshKey] = useState(0);

  // Detect superadmin viewing a workspace they don't belong to
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const isSuperAdminGuest = useMemo(() => {
    if (!user?.is_superadmin) return false;
    return !workspaces.some((w) => String(w.id) === String(workspaceId));
  }, [user, workspaces, workspaceId]);

  // Fetch workspaces if not provided by parent
  const fetchWorkspaces = useCallback(async (forceRefresh = false) => {
    try {
      const response = await betaAPI.getWorkspaces({ forceRefresh });
      const workspaceList = response.data?.workspaces || [];
      setWorkspaces(workspaceList);
      return workspaceList;
    } catch (err) {
      console.error("Failed to fetch workspaces", err);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!parentContext.workspaces?.length) {
      fetchWorkspaces();
    }
  }, [fetchWorkspaces, parentContext.workspaces]);

  const handleWorkspaceChange = useCallback(
    (newWorkspaceId) => {
      localStorage.setItem("selectedWorkspace", String(newWorkspaceId));
      navigate(`/v2/${newWorkspaceId}`);
    },
    [navigate]
  );

  const handleWorkspacesRefresh = useCallback(async () => {
    await fetchWorkspaces(true);
  }, [fetchWorkspaces]);

  // Handle workspace deletion - navigate to next available workspace
  const handleWorkspaceDeleted = useCallback(async () => {
    const freshWorkspaces = await fetchWorkspaces(true);

    if (freshWorkspaces.length > 0) {
      const nextWorkspace = freshWorkspaces[0];
      showToast.success(
        t("navigation.layout.workspaceDeletedSwitched", {
          name: nextWorkspace.name,
        })
      );
      navigate(`/v2/${nextWorkspace.id}`);
    } else {
      showToast.success(t("navigation.layout.workspaceDeleted"));
      navigate("/v2");
    }
  }, [fetchWorkspaces, navigate, t]);

  // Handle workspace update - refresh data
  const handleWorkspaceUpdated = useCallback(async () => {
    await fetchWorkspaces(true);
    setRefreshKey((prev) => prev + 1);
  }, [fetchWorkspaces]);

  return (
    <div className="min-h-screen overscroll-none bg-[#F8F9FA]">
      <BetaNavbar
        selectedWorkspaceId={workspaceId}
        onWorkspaceChange={handleWorkspaceChange}
        workspaces={workspaces}
        onWorkspacesRefresh={handleWorkspacesRefresh}
      />

      {isSuperAdminGuest && (
        <div className="flex items-center justify-center gap-2 bg-[#FEF3C7] px-4 py-2 text-sm font-medium text-[#92400E]">
          <AlertTriangle size={16} className="shrink-0" />
          {t("navigation.layout.superAdminGuestBanner")}
        </div>
      )}

      <main>
        <AnimatePresence mode="wait">
          <Outlet
            context={{
              workspaces,
              refreshKey,
              onWorkspaceDeleted: handleWorkspaceDeleted,
              onWorkspaceUpdated: handleWorkspaceUpdated,
              onWorkspacesRefresh: handleWorkspacesRefresh,
            }}
          />
        </AnimatePresence>
      </main>
    </div>
  );
};

export default V2Layout;
